import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import { authAPI } from '../services/api';
import * as sessionStore from '../services/sessionStore';
import { AdminUser, AdminRole } from '../types';

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isSuperadmin: boolean;
  isAiAgent: boolean;
  role: AdminRole | null;
  loading: boolean;
  sessions: sessionStore.StoredSession[];
  login: (username: string, password: string, remember?: boolean, org?: string) => Promise<void>;
  logout: () => Promise<void>;
  switchAccount: (memberId: number) => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [sessions, setSessions] = useState<sessionStore.StoredSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const queryClient = useQueryClient();

  const syncFromStore = () => {
    setSessions(sessionStore.listSessions());
    setUser(sessionStore.getActiveSession()?.user ?? null);
  };

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_active_member' && e.newValue !== e.oldValue) {
        // Another tab switched accounts. Reload so this tab can't keep
        // showing the previous account's tenant data.
        window.location.reload();
      } else if (e.key === 'admin_sessions') {
        syncFromStore();
      }
    };

    // Fired by the API layer after it drops the active session on a dead 401.
    const handleLogoutEvent = () => syncFromStore();

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('admin_logout', handleLogoutEvent);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('admin_logout', handleLogoutEvent);
    };
  }, []);

  useEffect(() => {
    if (initialized) return;

    const initAuth = async () => {
      try {
        setInitialized(true);
        // Sessions that predate the docs cookie get it on first load.
        sessionStore.syncDocsCookie();
        const active = sessionStore.getActiveSession();
        if (active) {
          setSessions(sessionStore.listSessions());
          try {
            const currentUser = await authAPI.getCurrentUser();
            sessionStore.updateUser(currentUser.id, currentUser);
            setUser(currentUser);
          } catch (error) {
            // A dead 401 already dropped the session via the interceptor;
            // for network errors keep the stored user so the app still loads.
            syncFromStore();
          }
        } else {
          syncFromStore();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username: string, password: string, remember?: boolean, org?: string) => {
    const userData = await authAPI.login(username, password, remember, org);
    // The previous account's tenant data must not survive into this one.
    queryClient.clear();
    setSessions(sessionStore.listSessions());
    setUser(userData);
  };

  const logout = async () => {
    const nextId = await authAPI.logout();
    queryClient.clear();
    if (nextId !== null) {
      // Another saved account took over — full reload by design, so nothing
      // from the logged-out account's tenant survives in memory.
      // "/" routes through DefaultRedirect, which picks the role-aware page.
      window.location.assign('/');
      return;
    }
    syncFromStore();
  };

  const switchAccount = (memberId: number) => {
    if (memberId === sessionStore.getActiveMemberId()) return;
    sessionStore.setActive(memberId);
    // Full reload by design: clears react-query caches and all in-memory
    // state so nothing leaks between tenants.
    window.location.assign('/');
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.is_superadmin) return true;
    return (user.permissions ?? []).includes(permission);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isSuperadmin: user?.is_superadmin ?? false,
    isAiAgent: user?.role === 'ai_agent',
    role: user?.role ?? null,
    loading,
    sessions,
    login,
    logout,
    switchAccount,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
